import { useContext, useState } from "react";
import { postSearchSalesRecordAPI, postUpdateSalesRecordAndPrintQrAPI, postUpdateSalesRecordAPI } from "../../../../http/api/SalesRecordCollectiveEntryAPI";
import { useEntrySearchBox } from "./useEntrySearchBox";
import { useNavigate } from "react-router-dom";
import { ScreenStatusContext } from "../../../../context/ScreenStatusContext";
import { useQrPreviewDialog } from "./useQrPreviewDialog";
import { useHistoryItems } from "./useHistoryItems";
import { flushSync } from "react-dom";

/**
 * 販売原票一括入力　画面情報管理hook
 */
export const useSalesRecordCollectiveEntry = () => {

  const [entryProducts, setEntryProducts] = useState([]);
  const [page, setPage] = useState(1);

  const {isChanged, setChanged} = useContext(ScreenStatusContext);

  const navigate = useNavigate();

  const searchBoxStates = useEntrySearchBox();
  const qrPreviewDialogStates = useQrPreviewDialog();
  
  const {historyItems, changeHistoryItem, clearHistoryItems} = useHistoryItems();

  /**
   * 検索
   * @param  searchCondition 検索条件 
   */
  const search = async (searchCondition) => {

    try {
      const searchSalesRecordRes = await postSearchSalesRecordAPI({
        searchCondition: {
          ...searchCondition, 
          commodityOwnerCd: searchCondition.commodityOwner?.commodityOwnerCd,
          productId: searchCondition.product?.productId,
          directorEmployeeId: searchCondition.directorEmployee?.directorEmployeeId,
        }
      });

      const { products } = searchSalesRecordRes.entry;
      setEntryProducts(createEntryProductList(products));
      setChanged(false);
      changePage(1);
      clearHistoryItems();
      searchBoxStates.memorizeCurrentSearchCondition();
    } catch(e) {
      console.log(e);
      alert("販売原票・発注情報の取得に失敗しました。")
    }
  }

  const searchWhenConfirmed = async (searchCondition) => {
    if (isChanged && !await submitWhenConfirmed()) {
      searchBoxStates.resetPrevSearchCondition();
      return;
    }
    
    return search(searchCondition);
  }

  const submit = async () => {

    try {
      const res = await postUpdateSalesRecordAPI({
        products: entryProducts.map((prod) => ({
          ...prod,
          historyItems: historyItems.filter((item) => item.salesRecordNo == prod.salesRecordNo)
        })),
        commodityOwnerCd: searchBoxStates.searchCondition.commodityOwner.commodityOwnerCd,
      });

      alert("販売原票を更新しました。")

      await search({...searchBoxStates.searchCondition});
      setChanged(false);
    } catch(e) {
      console.log(e);
      alert("販売原票更新に失敗しました。")
    }
  }

  const submitAndPrintQr = async () => {

    try {

      const pdfBytes = await postUpdateSalesRecordAndPrintQrAPI({
        products: entryProducts.map((prod) => ({
          ...prod,
          historyItems: historyItems.filter((item) => item.salesRecordNo == prod.salesRecordNo)
        })),
        commodityOwnerCd: searchBoxStates.searchCondition.commodityOwner.commodityOwnerCd,
      });

      const blob = new Blob([pdfBytes], {type: "application/pdf",  encoding: "UTF-8"});
      const url = URL.createObjectURL(blob);

      setChanged(false);
      qrPreviewDialogStates.setPdfUrl(url);

      await qrPreviewDialogStates.open();      
      await search({...searchBoxStates.searchCondition});

    } catch(e) {
      console.log(e);
      alert("販売原票更新・QR作成に失敗しました。")
    }

  }

  /**
   * 明細行入力変更
   * @param productIndex 商品行番号
   * @param detailIndex 明細行番号
   */
  const changeEntryDetailValue = (productIndex, detailIndex) => (propValueArray) => {

    const hasNewAcceptanceLabelNo 
      = Object.keys(propValueArray).find((propName) => propName === "acceptanceLabelNo");

    const hasNewPartition
      = Object.keys(propValueArray).find((propName) => propName === "partition");

    const hasNewDirectorEmployeeId
      = Object.keys(propValueArray).find((propName) => propName === "directorEmployeeId");

    const originId
      = entryProducts.find((prod) => prod.productIndex == productIndex)
          .details.find((dtl) => dtl.detailIndex == detailIndex)
          .originId;

    setEntryProducts((prev) => 
      prev.map((prod) => 
        prod.productIndex === productIndex
          ? {
              ...prod,
              details: prod.details.map((detail) => 
                detail.detailIndex === detailIndex
                  ? {...detail, ...propValueArray}
                  : {...detail, 
                    acceptanceLabelNo: 
                      hasNewAcceptanceLabelNo && detail.originId == originId
                        ? propValueArray.acceptanceLabelNo
                        : detail.acceptanceLabelNo,
                    partition:
                      hasNewPartition && detail.originId == originId
                        ? propValueArray.partition
                        : detail.partition,
                    directorEmployeeId:
                      hasNewDirectorEmployeeId && detail.originId == originId
                        ? propValueArray.directorEmployeeId
                        : detail.directorEmployeeId,
                  }
              )
            }
          : prod
      )
    )

    if (entryProducts[productIndex].salesRecordNo) {
      for (const propName in propValueArray) {
        if (
          ['originId', 'acceptanceLabelNo', 'partition', 'directorEmployeeId', 'rankId', 'productClassId', 'packingId', 'weight', 'quantity', 'purchacePrice']
            .find((pn) => pn == propName)
        ) {
          changeHistoryItem(
            entryProducts[productIndex].salesRecordNo,
            entryProducts[productIndex].details[detailIndex].salesDetailId,
            propName,
            propValueArray[propName]
          );
        }
      }
      
    }

    setChanged(true);
  };
 
  /**
   * 明細追加
   */
  const addDetail = (productIndex, detailIndex) => () => {
    const detailToCopy
      = entryProducts
          .find((prod) => prod.productIndex === productIndex)
          .details
          .find((dtl) => dtl.detailIndex === detailIndex);

    
    setEntryProducts((prev) => 
      createEntryProductList(
        prev.map((prod) => 
          prod.productIndex === productIndex
            ? {
                ...prod,
                details: [
                  ...prod.details.slice(0, detailIndex + 1),
                  {...detailToCopy,
                    salesDetailId: null
                  },
                  ...prod.details.slice(detailIndex + 1),
                ]
              }
            : prod
        )
      )
    );
  }

  /**
   * 明細削除
   */
  const deleteDetail = (productIndex, detailIndex) => () => {
    
    setEntryProducts((prev) => 
      createEntryProductList(
        prev.map((prod) => 
          prod.productIndex === productIndex
            ? {
                ...prod,
                details: [
                  ...prod.details.slice(0, detailIndex),
                  ...prod.details.slice(detailIndex + 1),
                ]
              }
            : prod
        )
      )
    )
  }

  /**
   * 明細毎印刷チェック変更
   */
  const changeNeedsPrintForEachDetail = (productIndex) => (needsPrintForEachDetail) => {
    setEntryProducts((prev) => 
      prev.map((prod) => 
        prod.productIndex === productIndex
          ? {
              ...prod,
              needsPrintForEachDetail
            }
          : prod
      )
    )
  }

  /**
   * ページ変更
   * @param page ページ数
   */
  const changePage = (page) => {
    // setPage(page);
    flushSync(() => {
        setPage(page);
    });

  }

  /**
   * 販売原票表示
   * @param salesRecordNo 販売原票番号
   * @param salesRecordType 販売原票種別(旅物/在庫)
   */
  const showSalesRecord = async (salesRecordNo, salesRecordType) => {
    if (isChanged && !await submitWhenConfirmed()) {
      searchBoxStates.resetPrevSearchCondition();
      return;
    }
    navigate(
      `/company/sales_record/register_entry/${salesRecordType}`,
      {state: {salesRecordNo}}
    );    
  }

  /**
   * 確定確認をユーザに行い、OKを押したら更新する
   */
  const submitWhenConfirmed = async () => {
    
    if (!window.confirm("注文情報の入力が変更されています。確定しますか?")) {
      return false;
    }

    try {
      await postUpdateSalesRecordAPI({
        products: entryProducts.map((prod) => ({
          ...prod,
          historyItems: historyItems.filter((item) => item.salesRecordNo == prod.salesRecordNo)
        })),
        commodityOwnerCd: searchBoxStates.searchCondition.commodityOwner.commodityOwnerCd
      });
      
      return true;

    } catch (e) {
      console.log(e);
      alert("販売原票の更新に失敗しました。")
      return false;
    } 

  };

  const hasError = () => 
    entryProducts.some((product) => 
      product.details.some((detail) => detail.error)
    );

  const errorMessages = (() => {
    return hasError()
      ? ["入力にエラーがあります。"]
      : [];
  })();

  return {
    entryProducts,
    entryTableControl: {
      submitAndPrintQr,
      submit,
      changeNeedsPrintForEachDetail,
      changeEntryDetailValue,
      page,
      changePage,
      showSalesRecord,
      addDetail,
      deleteDetail,
      disabledSubmit: hasError() || entryProducts.length == 0
    },
    search: searchWhenConfirmed,
    searchBoxStates,
    errorMessages,
    hasError,
    qrPreviewDialogStates
  };
}


const createEntryProductList = (products) => {
  return products.map((product, productIndex) => ({
    ...product,
    productIndex,
    details: product.details.map((detail, detailIndex) => ({
      ...detail,
      detailIndex,
      error: false
    })),

  }))
}